import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { 
  Phone, 
  Users, 
  Calendar, 
  BarChart3, 
  Settings, 
  LogOut, 
  Plus,
  User,
  Clock,
  AlertCircle,
  CheckCircle2,
  Heart,
  MessageCircle,
  Send,
  Bot,
  History,
  X,
  Edit,
  UserCheck,
  PhoneCall,
  CalendarDays,
  Timer,
  MapPin,
  Mail,
  Save,
  Trash2,
  Eye,
  UserPlus,
  Shield,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      // Verify token and get user data
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // You could add a verify token endpoint here
    }
    setLoading(false);
  }, [token]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'volunteer',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (isLogin) {
        login(response.data.user, response.data.access_token);
        toast.success('Connexion réussie !');
      } else {
        toast.success('Inscription réussie ! Vous pouvez maintenant vous connecter.');
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0 bg-white/80 backdrop-blur-sm">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl text-slate-800 font-bold">Brin de causette</CardTitle>
          <CardDescription className="text-slate-600">
            {isLogin ? 'Connectez-vous à votre compte' : 'Créez votre compte bénévole'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name">Nom complet</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Rôle</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="volunteer">Bénévole</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone (optionnel)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700" disabled={loading}>
              {loading ? 'Chargement...' : (isLogin ? 'Se connecter' : "S'inscrire")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {isLogin ? "Pas de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState({
    total_contacts: 12,
    total_volunteers: 8,
    pending_calls: 5,
    completed_calls: 47,
    high_priority_contacts: 3
  }); // Stats démo pour éviter erreurs API

  const user = { role: 'admin', name: 'Démo Admin' }; // User démo

  // Pas de fetch API en mode démo
  // useEffect(() => {
  //   fetchStats();
  // }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Tableau de bord</h1>
        <Badge variant="secondary" className="px-3 py-1">
          {user?.role === 'admin' ? 'Administrateur' : 'Bénévole'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
          onClick={() => onNavigate('contacts')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Contacts total</p>
                <p className="text-3xl font-bold text-blue-800">{stats.total_contacts || 0}</p>
                <p className="text-xs text-blue-600 mt-1">Cliquez pour voir la liste</p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        {user?.role === 'admin' && (
          <Card 
            className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => onNavigate('volunteers')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Bénévoles</p>
                  <p className="text-3xl font-bold text-green-800">{stats.total_volunteers || 0}</p>
                  <p className="text-xs text-green-600 mt-1">Cliquez pour gérer</p>
                </div>
                <User className="h-12 w-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
        )}

        <Card 
          className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
          onClick={() => onNavigate('calls')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Appels en attente</p>
                <p className="text-3xl font-bold text-orange-800">{stats.pending_calls || 0}</p>
                <p className="text-xs text-orange-600 mt-1">Cliquez pour l'historique</p>
              </div>
              <Clock className="h-12 w-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
          onClick={() => onNavigate('calendar')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Appels réalisés</p>
                <p className="text-3xl font-bold text-purple-800">{stats.completed_calls || 0}</p>
                <p className="text-xs text-purple-600 mt-1">Cliquez pour le planning</p>
              </div>
              <CheckCircle2 className="h-12 w-12 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        {user?.role === 'admin' && stats.high_priority_contacts > 0 && (
          <Card 
            className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 md:col-span-2 lg:col-span-4 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => onNavigate('contacts')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Contacts prioritaires nécessitant un suivi</p>
                  <p className="text-3xl font-bold text-red-800">{stats.high_priority_contacts}</p>
                  <p className="text-xs text-red-600 mt-1">Cliquez pour voir les contacts urgents</p>
                </div>
                <AlertCircle className="h-12 w-12 text-red-500" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Unified Calls & Planning Component
const CallsAndPlanning = () => {
  const [calls, setCalls] = useState([
    {
      id: "call-1",
      contact_id: "demo-1",
      contact_name: "Marie Dupont",
      contact_phone: "+33145678901",
      volunteer_id: "volunteer-1",
      volunteer_name: "Sophie Dubois",
      scheduled_date: "2025-08-30T10:30:00Z",
      actual_date: "2025-08-30T10:35:00Z",
      duration_minutes: 25,
      status: "completed",
      notes: "Très bonne conversation. Marie était en forme, a parlé de son jardin et de ses tomates. Elle semble moins isolée.",
      follow_up_needed: false
    },
    {
      id: "call-2", 
      contact_id: "demo-2",
      contact_name: "Pierre Martin",
      contact_phone: "+33156789012",
      volunteer_id: "volunteer-2",
      volunteer_name: "Jean Lefebvre",
      scheduled_date: "2025-08-30T14:00:00Z",
      actual_date: "2025-08-30T14:05:00Z",
      duration_minutes: 35,
      status: "completed",
      notes: "Pierre était un peu mélancolique aujourd'hui. A évoqué des souvenirs de son épouse. Besoin de plus d'attention.",
      follow_up_needed: true
    },
    {
      id: "call-3",
      contact_id: "demo-3", 
      contact_name: "Jeanne Moreau",
      contact_phone: "+33167890123",
      volunteer_id: "volunteer-1",
      volunteer_name: "Sophie Dubois",
      scheduled_date: "2025-08-31T09:00:00Z",
      actual_date: null,
      duration_minutes: null,
      status: "scheduled",
      notes: "Première prise de contact, personne très isolée",
      follow_up_needed: false
    },
    {
      id: "call-4",
      contact_id: "demo-1",
      contact_name: "Marie Dupont",
      contact_phone: "+33145678901", 
      volunteer_id: "volunteer-3",
      volunteer_name: "Marie Bernard",
      scheduled_date: "2025-08-29T15:30:00Z",
      actual_date: null,
      duration_minutes: null,
      status: "missed",
      notes: "Pas de réponse au téléphone. À rappeler demain.",
      follow_up_needed: true
    },
    {
      id: "call-5",
      contact_id: "demo-2",
      contact_name: "Pierre Martin",
      contact_phone: "+33156789012",
      volunteer_id: "volunteer-2", 
      volunteer_name: "Jean Lefebvre",
      scheduled_date: "2025-09-01T11:00:00Z",
      actual_date: null,
      duration_minutes: null,
      status: "scheduled",
      notes: "Suivi régulier, personne âgée nécessitant attention",
      follow_up_needed: false
    }
  ]);

  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'scheduled', 'completed', 'missed'
  const [showAddCallModal, setShowAddCallModal] = useState(false);
  const [showEditCallModal, setShowEditCallModal] = useState(false);
  const [editingCall, setEditingCall] = useState(null);
  const [newCall, setNewCall] = useState({
    contact_id: '',
    volunteer_id: '',
    scheduled_date: '',
    scheduled_time: '',
    notes: ''
  });

  // Données pour les dropdowns
  const contacts = [
    { id: "demo-1", name: "Marie Dupont", phone: "+33145678901" },
    { id: "demo-2", name: "Pierre Martin", phone: "+33156789012" },
    { id: "demo-3", name: "Jeanne Moreau", phone: "+33167890123" }
  ];

  const volunteers = [
    { id: "volunteer-1", name: "Sophie Dubois" },
    { id: "volunteer-2", name: "Jean Lefebvre" },
    { id: "volunteer-3", name: "Marie Bernard" }
  ];

  // Filtrage des appels
  const filteredCalls = calls.filter(call => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'scheduled') return call.status === 'scheduled';
    if (filterStatus === 'completed') return call.status === 'completed';
    if (filterStatus === 'missed') return call.status === 'missed';
    return true;
  });

  // Fonction pour composer un numéro
  const makeCall = (phoneNumber, contactName) => {
    // Détection du device
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    if (isMobile) {
      // Sur mobile : ouvre le composeur téléphonique
      window.location.href = `tel:${phoneNumber}`;
    } else {
      // Sur ordinateur : options multiples
      const options = [
        `Appeler ${contactName} au ${phoneNumber}`,
        '',
        '📱 Copier le numéro',
        '💻 Skype (si installé)',
        '📞 WhatsApp Web',
        '❌ Annuler'
      ].join('\n');
      
      const choice = prompt(options + '\n\nChoisissez une option (1-4):');
      
      switch(choice) {
        case '1':
          navigator.clipboard.writeText(phoneNumber);
          toast.success(`Numéro ${phoneNumber} copié dans le presse-papiers`);
          break;
        case '2':
          window.open(`skype:${phoneNumber.replace(/\+/g, '00')}?call`);
          break;
        case '3':
          window.open(`https://web.whatsapp.com/send?phone=${phoneNumber.replace(/\+/g, '')}`);
          break;
        default:
          return;
      }
    }
  };

  const handleAddCall = async (e) => {
    e.preventDefault();
    const scheduledDateTime = new Date(`${newCall.scheduled_date}T${newCall.scheduled_time}`).toISOString();
    const contact = contacts.find(c => c.id === newCall.contact_id);
    const volunteer = volunteers.find(v => v.id === newCall.volunteer_id);
    
    const newCallObj = {
      id: `call-${Date.now()}`,
      contact_id: newCall.contact_id,
      contact_name: contact?.name,
      contact_phone: contact?.phone,
      volunteer_id: newCall.volunteer_id,
      volunteer_name: volunteer?.name,
      scheduled_date: scheduledDateTime,
      actual_date: null,
      duration_minutes: null,
      status: "scheduled",
      notes: newCall.notes,
      follow_up_needed: false
    };
    
    setCalls([...calls, newCallObj]);
    toast.success('Appel programmé avec succès ! (Mode démo)');
    setShowAddCallModal(false);
    setNewCall({
      contact_id: '',
      volunteer_id: '',
      scheduled_date: '',
      scheduled_time: '',
      notes: ''
    });
  };

  const handleEditCall = (call) => {
    const scheduleDate = new Date(call.scheduled_date);
    setEditingCall({
      ...call,
      scheduled_date: scheduleDate.toISOString().split('T')[0],
      scheduled_time: scheduleDate.toTimeString().slice(0, 5),
      actual_date: call.actual_date ? new Date(call.actual_date).toISOString().split('T')[0] : '',
      actual_time: call.actual_date ? new Date(call.actual_date).toTimeString().slice(0, 5) : ''
    });
    setShowEditCallModal(true);
  };

  const handleUpdateCall = async (e) => {
    e.preventDefault();
    const scheduledDateTime = new Date(`${editingCall.scheduled_date}T${editingCall.scheduled_time}`).toISOString();
    const actualDateTime = editingCall.actual_date && editingCall.actual_time 
      ? new Date(`${editingCall.actual_date}T${editingCall.actual_time}`).toISOString()
      : null;
    
    const updatedCalls = calls.map(call => 
      call.id === editingCall.id 
        ? { 
            ...editingCall, 
            scheduled_date: scheduledDateTime,
            actual_date: actualDateTime,
            duration_minutes: editingCall.duration_minutes ? parseInt(editingCall.duration_minutes) : null
          }
        : call
    );
    setCalls(updatedCalls);
    toast.success('Appel modifié avec succès ! (Mode démo)');
    setShowEditCallModal(false);
    setEditingCall(null);
  };

  const deleteCall = (callId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet appel ?')) {
      setCalls(calls.filter(call => call.id !== callId));
      toast.success('Appel supprimé ! (Mode démo)');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'missed': return 'bg-red-100 text-red-800 border-red-200';
      case 'rescheduled': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'scheduled': return 'Programmé';
      case 'missed': return 'Manqué';
      case 'rescheduled': return 'Reporté';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
      case 'scheduled': return <Clock className="w-4 h-4" />;
      case 'missed': return <X className="w-4 h-4" />;
      case 'rescheduled': return <Calendar className="w-4 h-4" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Appels & Planning</h1>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddCallModal(true)}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Programmer un appel
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setFilterStatus('all')}
          variant={filterStatus === 'all' ? 'default' : 'outline'}
          size="sm"
        >
          Tous ({calls.length})
        </Button>
        <Button
          onClick={() => setFilterStatus('scheduled')}
          variant={filterStatus === 'scheduled' ? 'default' : 'outline'}
          size="sm"
          className="border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          Programmés ({calls.filter(c => c.status === 'scheduled').length})
        </Button>
        <Button
          onClick={() => setFilterStatus('completed')}
          variant={filterStatus === 'completed' ? 'default' : 'outline'}
          size="sm"
          className="border-green-200 text-green-700 hover:bg-green-50"
        >
          Terminés ({calls.filter(c => c.status === 'completed').length})
        </Button>
        <Button
          onClick={() => setFilterStatus('missed')}
          variant={filterStatus === 'missed' ? 'default' : 'outline'}
          size="sm"
          className="border-red-200 text-red-700 hover:bg-red-50"
        >
          Manqués ({calls.filter(c => c.status === 'missed').length})
        </Button>
      </div>

      {/* Modal d'ajout d'appel */}
      {showAddCallModal && (
        <Dialog open={showAddCallModal} onOpenChange={setShowAddCallModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Programmer un nouvel appel</DialogTitle>
              <DialogDescription>
                Planifiez un appel entre un bénévole et un contact
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddCall} className="space-y-4">
              <div>
                <Label htmlFor="call-contact">Contact *</Label>
                <Select value={newCall.contact_id} onValueChange={(value) => setNewCall({...newCall, contact_id: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choisir un contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.phone})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="call-volunteer">Bénévole *</Label>
                <Select value={newCall.volunteer_id} onValueChange={(value) => setNewCall({...newCall, volunteer_id: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choisir un bénévole" />
                  </SelectTrigger>
                  <SelectContent>
                    {volunteers.map((volunteer) => (
                      <SelectItem key={volunteer.id} value={volunteer.id}>
                        {volunteer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="call-date">Date *</Label>
                  <Input
                    id="call-date"
                    type="date"
                    value={newCall.scheduled_date}
                    onChange={(e) => setNewCall({...newCall, scheduled_date: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="call-time">Heure *</Label>
                  <Input
                    id="call-time"
                    type="time"
                    value={newCall.scheduled_time}
                    onChange={(e) => setNewCall({...newCall, scheduled_time: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="call-notes">Notes</Label>
                <Textarea
                  id="call-notes"
                  value={newCall.notes}
                  onChange={(e) => setNewCall({...newCall, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                  placeholder="Instructions spéciales, contexte..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Programmer
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddCallModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal d'édition d'appel */}
      {showEditCallModal && editingCall && (
        <Dialog open={showEditCallModal} onOpenChange={setShowEditCallModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l'appel</DialogTitle>
              <DialogDescription>
                Appel entre {editingCall.volunteer_name} et {editingCall.contact_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateCall} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-scheduled-date">Date programmée *</Label>
                  <Input
                    id="edit-scheduled-date"
                    type="date"
                    value={editingCall.scheduled_date}
                    onChange={(e) => setEditingCall({...editingCall, scheduled_date: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-scheduled-time">Heure programmée *</Label>
                  <Input
                    id="edit-scheduled-time"
                    type="time"
                    value={editingCall.scheduled_time}
                    onChange={(e) => setEditingCall({...editingCall, scheduled_time: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-status">Statut</Label>
                <Select value={editingCall.status} onValueChange={(value) => setEditingCall({...editingCall, status: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Programmé</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="missed">Manqué</SelectItem>
                    <SelectItem value="rescheduled">Reporté</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(editingCall.status === 'completed' || editingCall.status === 'missed') && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-actual-date">Date réelle</Label>
                      <Input
                        id="edit-actual-date"
                        type="date"
                        value={editingCall.actual_date}
                        onChange={(e) => setEditingCall({...editingCall, actual_date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-actual-time">Heure réelle</Label>
                      <Input
                        id="edit-actual-time"
                        type="time"
                        value={editingCall.actual_time}
                        onChange={(e) => setEditingCall({...editingCall, actual_time: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {editingCall.status === 'completed' && (
                    <div>
                      <Label htmlFor="edit-duration">Durée (minutes)</Label>
                      <Input
                        id="edit-duration"
                        type="number"
                        value={editingCall.duration_minutes || ''}
                        onChange={(e) => setEditingCall({...editingCall, duration_minutes: e.target.value})}
                        className="mt-1"
                        placeholder="25"
                      />
                    </div>
                  )}
                </>
              )}
              <div>
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editingCall.notes || ''}
                  onChange={(e) => setEditingCall({...editingCall, notes: e.target.value})}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-follow-up"
                  checked={editingCall.follow_up_needed}
                  onChange={(e) => setEditingCall({...editingCall, follow_up_needed: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="edit-follow-up">Suivi nécessaire</Label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Sauvegarder
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditCallModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid gap-4">
        {filteredCalls.map((call) => (
          <Card key={call.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      {call.contact_name}
                      {call.status === 'scheduled' && (
                        <Button
                          onClick={() => makeCall(call.contact_phone, call.contact_name)}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 text-xs"
                        >
                          <PhoneCall className="w-3 h-3 mr-1" />
                          Appeler
                        </Button>
                      )}
                    </h3>
                    <p className="text-sm text-slate-500">
                      <PhoneCall className="w-3 h-3 inline mr-1" />
                      {call.contact_phone} • Bénévole: {call.volunteer_name}
                    </p>
                    <p className="text-sm text-slate-500">
                      Programmé: {new Date(call.scheduled_date).toLocaleString('fr-FR')}
                    </p>
                    {call.actual_date && (
                      <p className="text-sm text-slate-500">
                        Réalisé: {new Date(call.actual_date).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {call.duration_minutes && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Timer className="w-4 h-4" />
                      {call.duration_minutes} min
                    </div>
                  )}
                  <Badge className={getStatusColor(call.status) + " flex items-center gap-1"}>
                    {getStatusIcon(call.status)}
                    {getStatusLabel(call.status)}
                  </Badge>
                  {call.follow_up_needed && (
                    <AlertCircle className="w-5 h-5 text-orange-500" />
                  )}
                  <div className="flex gap-1">
                    <Button
                      onClick={() => handleEditCall(call)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => deleteCall(call.id)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {call.notes && (
                <div className="bg-slate-50 rounded-lg p-3 mt-3">
                  <p className="text-sm text-slate-700">{call.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCalls.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Phone className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              Aucun appel {filterStatus === 'all' ? '' : getStatusLabel(filterStatus).toLowerCase()}
            </h3>
            <p className="text-slate-500">
              {filterStatus === 'all' 
                ? 'Commencez par programmer un appel' 
                : `Changez de filtre ou programmez de nouveaux appels`
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Planning Component  
const Planning = () => {
  const [plannedCalls, setPlannedCalls] = useState([
    {
      id: "plan-1",
      contact_name: "Jeanne Moreau",
      volunteer_name: "Sophie Dubois", 
      date: "2025-08-31",
      time: "09:00",
      priority: "urgent",
      frequency: "Hebdomadaire",
      last_call: "2025-08-24",
      notes: "Première prise de contact, personne très isolée"
    },
    {
      id: "plan-2",
      contact_name: "Pierre Martin", 
      volunteer_name: "Jean Lefebvre",
      date: "2025-09-01",
      time: "11:00", 
      priority: "high",
      frequency: "Bi-hebdomadaire",
      last_call: "2025-08-30",
      notes: "Suivi régulier, personne âgée nécessitant attention"
    },
    {
      id: "plan-3",
      contact_name: "Marie Dupont",
      volunteer_name: "Marie Bernard",
      date: "2025-09-02", 
      time: "15:30",
      priority: "normal",
      frequency: "Hebdomadaire", 
      last_call: "2025-08-26",
      notes: "Conversation habituelle, jardinage"
    },
    {
      id: "plan-4",
      contact_name: "Henri Dubois",
      volunteer_name: "Sophie Dubois",
      date: "2025-09-03",
      time: "10:15",
      priority: "normal", 
      frequency: "Hebdomadaire",
      last_call: "2025-08-27",
      notes: "Ancien mécanicien, aime parler de voitures"
    },
    {
      id: "plan-5",
      contact_name: "Jeanne Moreau",
      volunteer_name: "Sophie Dubois",
      date: "2025-09-07", 
      time: "09:00",
      priority: "urgent",
      frequency: "Hebdomadaire",
      last_call: null,
      notes: "Suivi hebdomadaire - deuxième semaine"
    }
  ]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const isToday = (dateString) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  const isPast = (dateString) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString < today;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Planning des Appels</h1>
        <div className="flex gap-2">
          <Badge variant="secondary" className="px-3 py-1">
            {plannedCalls.filter(c => isToday(c.date)).length} aujourd'hui
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {plannedCalls.filter(c => c.priority === 'urgent').length} urgents
          </Badge>
        </div>
      </div>

      <div className="grid gap-4">
        {plannedCalls.map((call) => (
          <Card key={call.id} className={`hover:shadow-md transition-shadow ${
            isToday(call.date) ? 'ring-2 ring-blue-500 bg-blue-50/30' : 
            isPast(call.date) ? 'bg-red-50/30' : ''
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isToday(call.date) ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                    isPast(call.date) ? 'bg-gradient-to-br from-red-500 to-red-600' :
                    'bg-gradient-to-br from-green-500 to-emerald-600'
                  }`}>
                    <CalendarDays className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">{call.contact_name}</h3>
                    <p className="text-sm text-slate-500">Bénévole: {call.volunteer_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {new Date(call.date + 'T' + call.time).toLocaleString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric', 
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Fréquence: {call.frequency}
                      {call.last_call && ` • Dernier appel: ${new Date(call.last_call).toLocaleDateString('fr-FR')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isToday(call.date) && (
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Aujourd'hui
                    </Badge>
                  )}
                  {isPast(call.date) && (
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      En retard
                    </Badge>
                  )}
                  <Badge className={getPriorityColor(call.priority)}>
                    {call.priority === 'urgent' ? 'Urgent' : call.priority === 'high' ? 'Élevée' : 'Normale'}
                  </Badge>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-700">{call.notes}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Users Management Component
const UsersManagement = () => {
  const [users, setUsers] = useState([
    {
      id: "admin-1",
      name: "Démo Admin",
      email: "admin@brin.fr",
      role: "admin",
      phone: "+33123456789",
      is_active: true,
      created_at: "2025-01-15",
      last_login: "2025-08-30T10:30:00Z"
    },
    {
      id: "admin-2",
      name: "Marie Directrice",
      email: "marie.directrice@brin.fr", 
      role: "admin",
      phone: "+33145678901",
      is_active: true,
      created_at: "2024-11-20",
      last_login: "2025-08-29T15:45:00Z"
    },
    {
      id: "volunteer-1",
      name: "Sophie Dubois",
      email: "sophie.dubois@email.com",
      role: "volunteer", 
      phone: "+33612345678",
      is_active: true,
      created_at: "2023-06-15",
      last_login: "2025-08-30T09:15:00Z"
    },
    {
      id: "volunteer-2",
      name: "Jean Lefebvre",
      email: "jean.lefebvre@email.com",
      role: "volunteer",
      phone: "+33698765432", 
      is_active: true,
      created_at: "2022-03-10",
      last_login: "2025-08-29T14:20:00Z"
    }
  ]);

  const [filterRole, setFilterRole] = useState('all'); // 'all', 'admin', 'volunteer'
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'volunteer',
    phone: ''
  });

  // Filtrage des utilisateurs
  const filteredUsers = users.filter(user => {
    if (filterRole === 'all') return true;
    if (filterRole === 'admin') return user.role === 'admin';
    if (filterRole === 'volunteer') return user.role === 'volunteer';
    return true;
  });

  const handleAddUser = async (e) => {
    e.preventDefault();
    const newUserObj = {
      id: `user-${Date.now()}`,
      ...newUser,
      is_active: true,
      created_at: new Date().toISOString().split('T')[0],
      last_login: null
    };
    setUsers([...users, newUserObj]);
    toast.success('Utilisateur ajouté avec succès ! (Mode démo)');
    setShowAddUserModal(false);
    setNewUser({
      name: '',
      email: '',
      password: '',
      role: 'volunteer',
      phone: ''
    });
  };

  const handleEditUser = (user) => {
    setEditingUser({...user});
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    const updatedUsers = users.map(user => 
      user.id === editingUser.id ? editingUser : user
    );
    setUsers(updatedUsers);
    toast.success('Utilisateur modifié avec succès ! (Mode démo)');
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  const toggleUserStatus = (userId) => {
    setUsers(users.map(user => 
      user.id === userId 
        ? { ...user, is_active: !user.is_active }
        : user
    ));
    toast.success('Statut utilisateur modifié ! (Mode démo)');
  };

  const deleteUser = (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      setUsers(users.filter(user => user.id !== userId));
      toast.success('Utilisateur supprimé ! (Mode démo)');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Gestion des Utilisateurs</h1>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddUserModal(true)}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Nouvel utilisateur
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setFilterRole('all')}
          variant={filterRole === 'all' ? 'default' : 'outline'}
          size="sm"
        >
          Tous ({users.length})
        </Button>
        <Button
          onClick={() => setFilterRole('admin')}
          variant={filterRole === 'admin' ? 'default' : 'outline'}
          size="sm"
          className="border-purple-200 text-purple-700 hover:bg-purple-50"
        >
          Admins ({users.filter(u => u.role === 'admin').length})
        </Button>
        <Button
          onClick={() => setFilterRole('volunteer')}
          variant={filterRole === 'volunteer' ? 'default' : 'outline'}
          size="sm"
          className="border-green-200 text-green-700 hover:bg-green-50"
        >
          Bénévoles ({users.filter(u => u.role === 'volunteer').length})
        </Button>
      </div>

      {/* Modal d'ajout d'utilisateur */}
      {showAddUserModal && (
        <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Créez un nouveau compte administrateur ou bénévole
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <Label htmlFor="user-name">Nom complet *</Label>
                <Input
                  id="user-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="user-email">Email *</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="user-password">Mot de passe *</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="user-role">Rôle</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="volunteer">Bénévole</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="user-phone">Téléphone</Label>
                <Input
                  id="user-phone"
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Créer
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddUserModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal d'édition d'utilisateur */}
      {showEditUserModal && editingUser && (
        <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifier l'utilisateur</DialogTitle>
              <DialogDescription>
                Modifiez les informations de {editingUser.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label htmlFor="edit-user-name">Nom complet *</Label>
                <Input
                  id="edit-user-name"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-user-email">Email *</Label>
                <Input
                  id="edit-user-email"
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-user-role">Rôle</Label>
                <Select value={editingUser.role} onValueChange={(value) => setEditingUser({...editingUser, role: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="volunteer">Bénévole</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-user-phone">Téléphone</Label>
                <Input
                  id="edit-user-phone"
                  type="tel"
                  value={editingUser.phone}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Sauvegarder
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditUserModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid gap-4">
        {filteredUsers.map((user) => (
          <Card key={user.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    user.role === 'admin' 
                      ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                      : 'bg-gradient-to-br from-green-500 to-emerald-600'
                  }`}>
                    {user.role === 'admin' ? <Shield className="w-6 h-6 text-white" /> : <UserCheck className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      {user.name}
                      <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}>
                        {user.role === 'admin' ? 'Admin' : 'Bénévole'}
                      </Badge>
                      <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{user.email}</span>
                    </div>
                    {user.phone && (
                      <div className="flex items-center gap-2">
                        <PhoneCall className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{user.phone}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      Membre depuis: {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      {user.last_login && (
                        <span className="ml-4">
                          Dernière connexion: {new Date(user.last_login).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleEditUser(user)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => toggleUserStatus(user.id)}
                    variant={user.is_active ? "destructive" : "default"}
                    size="sm"
                  >
                    {user.is_active ? 'Désactiver' : 'Activer'}
                  </Button>
                  <Button
                    onClick={() => deleteUser(user.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <UserCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              Aucun {filterRole === 'admin' ? 'administrateur' : filterRole === 'volunteer' ? 'bénévole' : 'utilisateur'}
            </h3>
            <p className="text-slate-500">
              {filterRole === 'all' 
                ? 'Commencez par créer des comptes utilisateurs' 
                : `Changez de filtre ou créez un nouveau ${filterRole === 'admin' ? 'administrateur' : 'bénévole'}`
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
const Volunteers = () => {
  const [volunteers, setVolunteers] = useState([
    {
      id: "volunteer-1",
      name: "Sophie Dubois",
      email: "sophie.dubois@email.com",
      phone: "+33612345678",
      age: 45,
      address: "12 Rue des Lilas, Paris 15e",
      experience: "2 ans",
      availability: "Lundi, Mercredi, Vendredi matin",
      skills: "Écoute active, expérience avec personnes âgées",
      notes: "Très empathique, excellente avec les personnes isolées. Ancienne infirmière.",
      is_active: true,
      joined_date: "2023-06-15",
      assigned_contacts: [
        { id: "demo-1", name: "Marie Dupont", priority: "normal" },
        { id: "demo-3", name: "Jeanne Moreau", priority: "urgent" }
      ]
    },
    {
      id: "volunteer-2", 
      name: "Jean Lefebvre",
      email: "jean.lefebvre@email.com",
      phone: "+33698765432",
      age: 52,
      address: "34 Avenue de la République, Lyon",
      experience: "3 ans",
      availability: "Mardi, Jeudi après-midi, Weekend",
      skills: "Patience, connaissance en histoire, jeux de société",
      notes: "Professeur à la retraite, excellent pour conversations intellectuelles.",
      is_active: true,
      joined_date: "2022-03-10",
      assigned_contacts: [
        { id: "demo-2", name: "Pierre Martin", priority: "high" }
      ]
    },
    {
      id: "volunteer-3",
      name: "Marie Bernard", 
      email: "marie.bernard@email.com",
      phone: "+33687654321",
      age: 38,
      address: "78 Boulevard Saint-Michel, Marseille",
      experience: "1 an",
      availability: "Flexible, principalement après-midi",
      skills: "Multilingue (français, italien), cuisine, jardinage",
      notes: "Nouvelle bénévole très motivée, spécialisée dans l'accompagnement culinaire.",
      is_active: true,
      joined_date: "2024-01-20",
      assigned_contacts: []
    }
  ]);

  // Contacts disponibles pour assignation
  const availableContacts = [
    { id: "demo-1", name: "Marie Dupont", priority: "normal" },
    { id: "demo-2", name: "Pierre Martin", priority: "high" },
    { id: "demo-3", name: "Jeanne Moreau", priority: "urgent" }
  ];

  const [showAddVolunteerModal, setShowAddVolunteerModal] = useState(false);
  const [showEditVolunteerModal, setShowEditVolunteerModal] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [newVolunteer, setNewVolunteer] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    address: '',
    experience: '',
    availability: '',
    skills: '',
    notes: ''
  });

  const handleAddVolunteer = async (e) => {
    e.preventDefault();
    const newVolunteerObj = {
      id: `volunteer-${Date.now()}`,
      ...newVolunteer,
      age: newVolunteer.age ? parseInt(newVolunteer.age) : null,
      is_active: true,
      joined_date: new Date().toISOString().split('T')[0],
      assigned_contacts: []
    };
    setVolunteers([...volunteers, newVolunteerObj]);
    toast.success('Bénévole ajouté avec succès ! (Mode démo)');
    setShowAddVolunteerModal(false);
    setNewVolunteer({
      name: '',
      email: '',
      phone: '',
      age: '',
      address: '',
      experience: '',
      availability: '',
      skills: '',
      notes: ''
    });
  };

  const handleEditVolunteer = (volunteer) => {
    setEditingVolunteer({
      ...volunteer,
      age: volunteer.age?.toString() || ''
    });
    setShowEditVolunteerModal(true);
  };

  const updateVolunteerAssignments = (contactId, isAssigned) => {
    const updatedAssignments = isAssigned 
      ? [...editingVolunteer.assigned_contacts.filter(c => c.id !== contactId)]
      : [...editingVolunteer.assigned_contacts, availableContacts.find(c => c.id === contactId)];
    
    setEditingVolunteer({
      ...editingVolunteer,
      assigned_contacts: updatedAssignments
    });
  };

  const handleUpdateVolunteer = async (e) => {
    e.preventDefault();
    const updatedVolunteers = volunteers.map(volunteer => 
      volunteer.id === editingVolunteer.id 
        ? { 
            ...editingVolunteer, 
            age: editingVolunteer.age ? parseInt(editingVolunteer.age) : null 
          }
        : volunteer
    );
    setVolunteers(updatedVolunteers);
    toast.success('Bénévole modifié avec succès ! (Mode démo)');
    setShowEditVolunteerModal(false);
    setEditingVolunteer(null);
  };

  const toggleVolunteerStatus = (volunteerId) => {
    setVolunteers(volunteers.map(volunteer => 
      volunteer.id === volunteerId 
        ? { ...volunteer, is_active: !volunteer.is_active }
        : volunteer
    ));
    toast.success('Statut du bénévole modifié ! (Mode démo)');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Bénévoles</h1>
        <div className="flex gap-2">
          <Badge variant="secondary" className="px-3 py-1">
            {volunteers.filter(v => v.is_active).length} actifs
          </Badge>
          <Button 
            onClick={() => setShowAddVolunteerModal(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un bénévole
          </Button>
        </div>
      </div>

      {/* Modal d'ajout de bénévole */}
      {showAddVolunteerModal && (
        <Dialog open={showAddVolunteerModal} onOpenChange={setShowAddVolunteerModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau bénévole</DialogTitle>
              <DialogDescription>
                Ajoutez un nouveau bénévole à l'équipe
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddVolunteer} className="space-y-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vol-name">Nom complet *</Label>
                  <Input
                    id="vol-name"
                    value={newVolunteer.name}
                    onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="vol-age">Âge</Label>
                  <Input
                    id="vol-age"
                    type="number"
                    value={newVolunteer.age}
                    onChange={(e) => setNewVolunteer({...newVolunteer, age: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="vol-email">Email *</Label>
                <Input
                  id="vol-email"
                  type="email"
                  value={newVolunteer.email}
                  onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="vol-phone">Téléphone</Label>
                <Input
                  id="vol-phone"
                  type="tel"
                  value={newVolunteer.phone}
                  onChange={(e) => setNewVolunteer({...newVolunteer, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="vol-address">Adresse</Label>
                <Input
                  id="vol-address"
                  value={newVolunteer.address}
                  onChange={(e) => setNewVolunteer({...newVolunteer, address: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="vol-experience">Expérience</Label>
                <Input
                  id="vol-experience"
                  value={newVolunteer.experience}
                  onChange={(e) => setNewVolunteer({...newVolunteer, experience: e.target.value})}
                  placeholder="ex: 2 ans, débutant..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="vol-availability">Disponibilités</Label>
                <Input
                  id="vol-availability"
                  value={newVolunteer.availability}
                  onChange={(e) => setNewVolunteer({...newVolunteer, availability: e.target.value})}
                  placeholder="ex: Lundi et mercredi matin"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="vol-skills">Compétences/Centres d'intérêt</Label>
                <Textarea
                  id="vol-skills"
                  value={newVolunteer.skills}
                  onChange={(e) => setNewVolunteer({...newVolunteer, skills: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="vol-notes">Notes</Label>
                <Textarea
                  id="vol-notes"
                  value={newVolunteer.notes}
                  onChange={(e) => setNewVolunteer({...newVolunteer, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                  Ajouter
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddVolunteerModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal d'édition de bénévole */}
      {showEditVolunteerModal && editingVolunteer && (
        <Dialog open={showEditVolunteerModal} onOpenChange={setShowEditVolunteerModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier le bénévole</DialogTitle>
              <DialogDescription>
                Modifiez les informations de {editingVolunteer.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateVolunteer} className="space-y-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-vol-name">Nom complet *</Label>
                  <Input
                    id="edit-vol-name"
                    value={editingVolunteer.name}
                    onChange={(e) => setEditingVolunteer({...editingVolunteer, name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-vol-age">Âge</Label>
                  <Input
                    id="edit-vol-age"
                    type="number"
                    value={editingVolunteer.age}
                    onChange={(e) => setEditingVolunteer({...editingVolunteer, age: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-vol-email">Email *</Label>
                <Input
                  id="edit-vol-email"
                  type="email"
                  value={editingVolunteer.email}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-phone">Téléphone</Label>
                <Input
                  id="edit-vol-phone"
                  type="tel"
                  value={editingVolunteer.phone}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-address">Adresse</Label>
                <Input
                  id="edit-vol-address"
                  value={editingVolunteer.address}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, address: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-experience">Expérience</Label>
                <Input
                  id="edit-vol-experience"
                  value={editingVolunteer.experience}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, experience: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-availability">Disponibilités</Label>
                <Input
                  id="edit-vol-availability"
                  value={editingVolunteer.availability}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, availability: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-skills">Compétences/Centres d'intérêt</Label>
                <Textarea
                  id="edit-vol-skills"
                  value={editingVolunteer.skills}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, skills: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="edit-vol-notes">Notes</Label>
                <Textarea
                  id="edit-vol-notes"
                  value={editingVolunteer.notes}
                  onChange={(e) => setEditingVolunteer({...editingVolunteer, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              {/* Section assignation des contacts */}
              <div className="space-y-3 border-t pt-4">
                <Label>Contacts assignés</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableContacts.map((contact) => {
                    const isAssigned = editingVolunteer.assigned_contacts?.some(c => c.id === contact.id);
                    return (
                      <div key={contact.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`contact-${contact.id}`}
                            checked={isAssigned}
                            onChange={(e) => updateVolunteerAssignments(contact.id, isAssigned)}
                            className="rounded"
                          />
                          <Label htmlFor={`contact-${contact.id}`} className="cursor-pointer">
                            {contact.name}
                          </Label>
                        </div>
                        <Badge className={
                          contact.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          contact.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-green-100 text-green-800'
                        }>
                          {contact.priority === 'urgent' ? 'Urgent' : 
                           contact.priority === 'high' ? 'Élevée' : 'Normale'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  Cochez les contacts à assigner à ce bénévole
                </p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Sauvegarder
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditVolunteerModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {volunteers.map((volunteer) => (
          <Card key={volunteer.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    volunteer.is_active 
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                      : 'bg-gradient-to-br from-gray-400 to-gray-500'
                  }`}>
                    <UserCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-slate-800">{volunteer.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Mail className="w-4 h-4" />
                      {volunteer.email}
                    </CardDescription>
                    {volunteer.phone && (
                      <CardDescription className="flex items-center gap-2">
                        <PhoneCall className="w-4 h-4" />
                        {volunteer.phone}
                      </CardDescription>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={volunteer.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {volunteer.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {volunteer.age && (
                <p className="text-sm text-slate-600">Âge: {volunteer.age} ans</p>
              )}
              {volunteer.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <p className="text-sm text-slate-600">{volunteer.address}</p>
                </div>
              )}
              <p className="text-sm text-slate-600">
                <span className="font-medium">Expérience:</span> {volunteer.experience}
              </p>
              <p className="text-sm text-slate-600">
                <span className="font-medium">Disponibilités:</span> {volunteer.availability}
              </p>
              {volunteer.skills && (
                <p className="text-sm text-slate-600 bg-blue-50 p-2 rounded-md">
                  <span className="font-medium">Compétences:</span> {volunteer.skills}
                </p>
              )}
              {volunteer.notes && (
                <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded-md">
                  {volunteer.notes}
                </p>
              )}
              
              {/* Contacts assignés */}
              <div className="border-t pt-3">
                <h4 className="font-medium text-sm text-slate-700 mb-2">
                  Contacts assignés ({volunteer.assigned_contacts.length})
                </h4>
                {volunteer.assigned_contacts.length > 0 ? (
                  <div className="space-y-1">
                    {volunteer.assigned_contacts.map((contact) => (
                      <div key={contact.id} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <span className="text-sm font-medium">{contact.name}</span>
                        <Badge className={
                          contact.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          contact.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-green-100 text-green-800'
                        }>
                          {contact.priority === 'urgent' ? 'Urgent' : 
                           contact.priority === 'high' ? 'Élevée' : 'Normale'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">Aucun contact assigné</p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleEditVolunteer(volunteer)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  onClick={() => toggleVolunteerStatus(volunteer.id)}
                  variant={volunteer.is_active ? "destructive" : "default"}
                  size="sm"
                  className="flex-1"
                >
                  {volunteer.is_active ? 'Désactiver' : 'Activer'}
                </Button>
              </div>
              
              <p className="text-xs text-slate-400 pt-2">
                Membre depuis: {new Date(volunteer.joined_date).toLocaleDateString('fr-FR')}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {volunteers.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <UserCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucun bénévole</h3>
            <p className="text-slate-500">
              Commencez par ajouter des bénévoles à votre équipe
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
const AIChat = () => {
  const [contacts, setContacts] = useState([
    {
      id: "demo-1",
      name: "Marie Dupont",
      phone: "+33145678901",
      age: 75,
      priority: "normal"
    },
    {
      id: "demo-2", 
      name: "Pierre Martin",
      phone: "+33156789012",
      age: 82,
      priority: "high"
    },
    {
      id: "demo-3",
      name: "Jeanne Moreau", 
      phone: "+33167890123",
      age: 78,
      priority: "urgent"
    }
  ]); // Contacts démo
  const [selectedContact, setSelectedContact] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const user = { role: 'admin', id: 'demo-admin' }; // User démo

  // Pas de fetch API en mode démo
  // useEffect(() => {
  //   fetchContacts();
  // }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchSessions(selectedContact.id);
    }
  }, [selectedContact]);

  useEffect(() => {
    if (currentSession) {
      fetchMessages(currentSession.id);
    }
  }, [currentSession]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    }
  };

  const fetchSessions = async (contactId) => {
    try {
      const response = await axios.get(`${API}/ai/sessions/${contactId}`);
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/ai/messages/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact || loading) return;

    setLoading(true);
    
    // MODE DÉMO - Simuler réponse IA
    const demoResponses = [
      "Bonjour ! C'est un plaisir de vous parler aujourd'hui. Comment allez-vous ?",
      "Merci de partager cela avec moi. Vos souvenirs sont vraiment précieux.",
      "Je comprends ce que vous ressentez. Vous n'êtes pas seul(e), nous sommes là pour vous.",
      "C'est merveilleux ! J'aimerais en savoir plus sur cette belle histoire.",
      "Vos petits-enfants ont de la chance de vous avoir. Parlez-moi d'eux !",
      "La solitude peut être difficile, mais rappelez-vous que vous êtes quelqu'un de spécial.",
      "Vos expériences de vie sont inspirantes. Continuez à les partager !"
    ];

    try {
      // Simuler délai de réponse IA
      await new Promise(resolve => setTimeout(resolve, 2000));

      const demoMessage = {
        id: `demo-msg-${Date.now()}`,
        contact_id: selectedContact.id,
        session_id: currentSession?.id || `demo-session-${Date.now()}`,
        message: newMessage,
        response: demoResponses[Math.floor(Math.random() * demoResponses.length)],
        timestamp: new Date().toISOString()
      };

      // Si nouvelle session, la créer
      if (!currentSession) {
        const newSession = {
          id: demoMessage.session_id,
          contact_id: selectedContact.id,
          contact_name: selectedContact.name,
          started_at: new Date().toISOString()
        };
        setCurrentSession(newSession);
        setSessions([newSession, ...sessions]);
      }

      // Ajouter le message
      setMessages(prev => [...prev, demoMessage]);
      setNewMessage('');
      toast.success('Message envoyé ! (Mode démo - IA simulée)');

    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const endSession = async () => {
    if (!currentSession) return;

    try {
      const response = await axios.post(`${API}/ai/sessions/${currentSession.id}/end`);
      toast.success('Session terminée avec succès');
      
      // Show summary
      if (response.data.summary) {
        toast.info(`Résumé: ${response.data.summary.substring(0, 100)}...`);
      }
      
      setCurrentSession(null);
      setMessages([]);
      fetchSessions(selectedContact.id);
    } catch (error) {
      toast.error('Erreur lors de la fin de session');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-full flex space-y-0 space-x-6">
      {/* Contact Selection Panel */}
      <div className="w-80 space-y-4">
        <h1 className="text-3xl font-bold text-slate-800">Chat IA</h1>
        <Card className="h-96 overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Sélectionner un contact
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-y-auto h-80">
              {contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setSelectedContact(contact)}
                  className={`w-full p-4 text-left border-b hover:bg-slate-50 transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="font-medium text-slate-800">{contact.name}</div>
                  <div className="text-sm text-slate-500">{contact.phone}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sessions History */}
        {selectedContact && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique des sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                onClick={startNewSession}
                className="w-full justify-start bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle conversation
              </Button>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => setCurrentSession(session)}
                    className={`w-full p-2 text-left text-sm rounded border hover:bg-slate-50 transition-colors ${
                      currentSession?.id === session.id ? 'bg-blue-50 border-blue-200' : 'border-slate-200'
                    }`}
                  >
                    <div className="font-medium">{new Date(session.started_at).toLocaleDateString('fr-FR')}</div>
                    <div className="text-xs text-slate-500">{session.total_messages} messages</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Chat Interface */}
      <div className="flex-1">
        {selectedContact ? (
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-3 flex-shrink-0 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Chat avec {selectedContact.name}</CardTitle>
                    <CardDescription>
                      {currentSession ? 'Session en cours' : 'Aucune session active'}
                    </CardDescription>
                  </div>
                </div>
                {currentSession && (
                  <Button 
                    onClick={endSession}
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Terminer
                  </Button>
                )}
              </div>
            </CardHeader>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
              {messages.length === 0 && !currentSession && (
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">Commencer une conversation</h3>
                  <p className="text-slate-500">
                    Démarrez une nouvelle conversation IA avec {selectedContact.name}
                  </p>
                </div>
              )}

              {messages.map((message) => (
                <div key={message.id} className="space-y-3">
                  {/* User Message */}
                  <div className="flex justify-end">
                    <div className="max-w-xs lg:max-w-md px-4 py-2 bg-blue-500 text-white rounded-l-lg rounded-tr-lg">
                      <p className="text-sm">{message.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start">
                    <div className="flex gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="max-w-xs lg:max-w-md px-4 py-2 bg-white border border-slate-200 rounded-r-lg rounded-tl-lg shadow-sm">
                        <p className="text-sm text-slate-800">{message.response}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Assistant IA • {new Date(message.timestamp).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="px-4 py-2 bg-white border border-slate-200 rounded-r-lg rounded-tl-lg shadow-sm">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="flex-shrink-0 p-4 border-t bg-white">
              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tapez votre message..."
                  className="flex-1 min-h-[40px] max-h-32 resize-none"
                  disabled={loading}
                />
                <Button 
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || loading}
                  className="px-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Appuyez sur Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne
              </p>
            </div>
          </Card>
        ) : (
          <Card className="h-full flex items-center justify-center">
            <CardContent className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sélectionnez un contact</h3>
              <p className="text-slate-500">
                Choisissez un contact dans la liste pour commencer une conversation IA
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Contacts Component
const Contacts = () => {
  const [contacts, setContacts] = useState([
    {
      id: "demo-1",
      name: "Marie Dupont", 
      phone: "+33145678901",
      age: 75,
      address: "123 Rue de la Paix, Paris",
      notes: [
        {
          id: "note-1",
          content: "Personne très gentille, aime parler de jardinage et de ses petits-enfants",
          date: "2025-01-15T10:30:00Z",
          author: "Admin"
        },
        {
          id: "note-2", 
          content: "Mention qu'elle a des problèmes de santé récents, à surveiller",
          date: "2025-02-20T14:15:00Z",
          author: "Sophie Dubois"
        }
      ],
      priority: "normal",
      assigned_volunteer_id: "volunteer-1",
      last_call_date: "2025-08-25T10:30:00Z"
    },
    {
      id: "demo-2",
      name: "Pierre Martin",
      phone: "+33156789012", 
      age: 82,
      address: "45 Avenue des Champs, Lyon",
      notes: [
        {
          id: "note-3",
          content: "Ancien professeur, passionné d'histoire. Nécessite un suivi régulier",
          date: "2024-11-10T09:00:00Z",
          author: "Admin"
        }
      ],
      priority: "high",
      assigned_volunteer_id: "volunteer-2",
      last_call_date: "2025-08-28T14:15:00Z"
    },
    {
      id: "demo-3",
      name: "Jeanne Moreau",
      phone: "+33167890123",
      age: 78,
      address: "67 Boulevard Saint-Germain, Paris", 
      notes: [
        {
          id: "note-4",
          content: "Personne isolée, famille éloignée. Besoin d'attention particulière",
          date: "2025-01-05T16:00:00Z",
          author: "Admin"
        }
      ],
      priority: "urgent",
      assigned_volunteer_id: "volunteer-1",
      last_call_date: null
    }
  ]); // Contacts démo
  const [volunteers, setVolunteers] = useState([
    { id: "volunteer-1", name: "Sophie Dubois" },
    { id: "volunteer-2", name: "Jean Lefebvre" },
    { id: "volunteer-3", name: "Marie Bernard" }
  ]); // Bénévoles démo
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    age: '',
    address: '',
    notes: '',
    priority: 'normal',
    call_frequency_days: 7
  });
  const user = { role: 'admin', id: 'demo-admin' }; // User démo

  // Pas de fetch API en mode démo
  // useEffect(() => {
  //   fetchContacts();
  //   if (user?.role === 'admin') {
  //     fetchVolunteers();
  //   }
  // }, [user]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    }
  };

  const fetchVolunteers = async () => {
    try {
      const response = await axios.get(`${API}/users/volunteers`);
      setVolunteers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des bénévoles');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    // Mode démo - ajouter localement sans API
    const newContactObj = {
      id: `demo-${Date.now()}`,
      ...newContact,
      age: newContact.age ? parseInt(newContact.age) : null,
      notes: newContact.notes ? [{
        id: `note-${Date.now()}`,
        content: newContact.notes,
        date: new Date().toISOString(),
        author: "Démo Admin"
      }] : [],
      assigned_volunteer_id: null,
      last_call_date: null
    };
    setContacts([...contacts, newContactObj]);
    toast.success('Contact ajouté avec succès ! (Mode démo)');
    setShowAddModal(false);
    setNewContact({
      name: '',
      phone: '',
      age: '',
      address: '',
      notes: '',
      priority: 'normal',
      call_frequency_days: 7
    });
  };

  const handleEditContact = (contact) => {
    setEditingContact({
      ...contact,
      age: contact.age?.toString() || '',
      newNote: '' // Nouveau champ pour ajouter une note
    });
    setShowEditModal(true);
  };

  const addNoteToContact = () => {
    if (!editingContact.newNote.trim()) return;
    
    const newNote = {
      id: `note-${Date.now()}`,
      content: editingContact.newNote,
      date: new Date().toISOString(),
      author: "Démo Admin" // En mode démo
    };
    
    setEditingContact({
      ...editingContact,
      notes: [...(editingContact.notes || []), newNote],
      newNote: ''
    });
  };

  const removeNote = (noteId) => {
    setEditingContact({
      ...editingContact,
      notes: editingContact.notes.filter(note => note.id !== noteId)
    });
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    // Mode démo - mise à jour locale
    const updatedContacts = contacts.map(contact => 
      contact.id === editingContact.id 
        ? { 
            ...editingContact, 
            age: editingContact.age ? parseInt(editingContact.age) : null 
          }
        : contact
    );
    setContacts(updatedContacts);
    toast.success('Contact modifié avec succès ! (Mode démo)');
    setShowEditModal(false);
    setEditingContact(null);
  };

  const makeCall = (phoneNumber, contactName) => {
    // Détection du device
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    if (isMobile) {
      // Sur mobile : ouvre le composeur téléphonique
      window.location.href = `tel:${phoneNumber}`;
    } else {
      // Sur ordinateur : options multiples
      const options = [
        `Appeler ${contactName} au ${phoneNumber}`,
        '',
        '📱 Copier le numéro',
        '💻 Skype (si installé)',
        '📞 WhatsApp Web',
        '❌ Annuler'
      ].join('\n');
      
      const choice = prompt(options + '\n\nChoisissez une option (1-4):');
      
      switch(choice) {
        case '1':
          navigator.clipboard.writeText(phoneNumber);
          toast.success(`Numéro ${phoneNumber} copié dans le presse-papiers`);
          break;
        case '2':
          window.open(`skype:${phoneNumber.replace(/\+/g, '00')}?call`);
          break;
        case '3':
          window.open(`https://web.whatsapp.com/send?phone=${phoneNumber.replace(/\+/g, '')}`);
          break;
        default:
          return;
      }
    }
  };

  const assignVolunteer = async (contactId, volunteerId) => {
    // Mode démo - mise à jour locale
    setContacts(contacts.map(contact => 
      contact.id === contactId 
        ? { ...contact, assigned_volunteer_id: volunteerId }
        : contact
    ));
    toast.success('Bénévole assigné avec succès ! (Mode démo)');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'Urgent';
      case 'high': return 'Élevée';
      default: return 'Normale';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800">Contacts</h1>
        {user?.role === 'admin' && (
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nouveau contact</DialogTitle>
                <DialogDescription>
                  Ajoutez une nouvelle personne à contacter régulièrement
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddContact} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    value={newContact.name}
                    onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="age">Âge</Label>
                  <Input
                    id="age"
                    type="number"
                    value={newContact.age}
                    onChange={(e) => setNewContact({...newContact, age: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="priority">Priorité</Label>
                  <Select value={newContact.priority} onValueChange={(value) => setNewContact({...newContact, priority: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Élevée</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={newContact.address}
                    onChange={(e) => setNewContact({...newContact, address: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={newContact.notes}
                    onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                    Ajouter
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                    Annuler
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Modal d'édition de contact */}
      {showEditModal && editingContact && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifier le contact</DialogTitle>
              <DialogDescription>
                Modifiez les informations de {editingContact.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateContact} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nom complet *</Label>
                <Input
                  id="edit-name"
                  value={editingContact.name}
                  onChange={(e) => setEditingContact({...editingContact, name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Téléphone *</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editingContact.phone}
                  onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-age">Âge</Label>
                <Input
                  id="edit-age"
                  type="number"
                  value={editingContact.age}
                  onChange={(e) => setEditingContact({...editingContact, age: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-priority">Priorité</Label>
                <Select value={editingContact.priority} onValueChange={(value) => setEditingContact({...editingContact, priority: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normale</SelectItem>
                    <SelectItem value="high">Élevée</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-address">Adresse</Label>
                <Input
                  id="edit-address"
                  value={editingContact.address}
                  onChange={(e) => setEditingContact({...editingContact, address: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              {/* Section Notes avec historique */}
              <div className="space-y-3">
                <Label>Historique des notes</Label>
                <div className="max-h-40 overflow-y-auto space-y-2 border rounded-md p-2">
                  {editingContact.notes && editingContact.notes.length > 0 ? (
                    editingContact.notes.map((note) => (
                      <div key={note.id} className="bg-slate-50 p-2 rounded border-l-2 border-blue-200">
                        <p className="text-sm text-slate-700 mb-1">{note.content}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500">
                            {new Date(note.date).toLocaleString('fr-FR')} - {note.author}
                          </p>
                          <Button
                            onClick={() => removeNote(note.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 p-1 h-6"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 italic">Aucune note pour l'instant</p>
                  )}
                </div>
                
                {/* Ajouter une nouvelle note */}
                <div className="space-y-2 border-t pt-3">
                  <Label htmlFor="new-note">Ajouter une nouvelle note</Label>
                  <div className="flex gap-2">
                    <Textarea
                      id="new-note"
                      value={editingContact.newNote || ''}
                      onChange={(e) => setEditingContact({...editingContact, newNote: e.target.value})}
                      className="flex-1"
                      placeholder="Saisir une nouvelle note de suivi..."
                      rows={2}
                    />
                    <Button
                      onClick={addNoteToContact}
                      type="button"
                      variant="outline"
                      disabled={!editingContact.newNote?.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                  Sauvegarder
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contacts.map((contact) => (
          <Card key={contact.id} className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
                    {contact.name}
                    <Button
                      onClick={() => makeCall(contact.phone, contact.name)}
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 text-xs"
                    >
                      <PhoneCall className="w-3 h-3 mr-1" />
                      Appeler
                    </Button>
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <Phone className="w-4 h-4" />
                    {contact.phone}
                  </CardDescription>
                </div>
                <Badge className={getPriorityColor(contact.priority)}>
                  {getPriorityLabel(contact.priority)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {contact.age && (
                <p className="text-sm text-slate-600">Âge: {contact.age} ans</p>
              )}
              {contact.address && (
                <p className="text-sm text-slate-600">Adresse: {contact.address}</p>
              )}
              {contact.notes && contact.notes.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Notes de suivi:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {contact.notes.slice(-2).map((note) => (
                      <div key={note.id} className="text-sm text-slate-600 bg-slate-50 p-2 rounded-md">
                        <p className="mb-1">{note.content}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(note.date).toLocaleDateString('fr-FR')} - {note.author}
                        </p>
                      </div>
                    ))}
                    {contact.notes.length > 2 && (
                      <p className="text-xs text-slate-500 italic">
                        +{contact.notes.length - 2} note(s) supplémentaire(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
              {contact.last_call_date && (
                <p className="text-sm text-slate-500">
                  Dernier appel: {new Date(contact.last_call_date).toLocaleDateString('fr-FR')}
                </p>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleEditContact(contact)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              </div>
              
              {user?.role === 'admin' && (
                <div className="pt-2">
                  <Label className="text-sm">Assigner à un bénévole:</Label>
                  <Select 
                    value={contact.assigned_volunteer_id || ''} 
                    onValueChange={(value) => assignVolunteer(contact.id, value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choisir un bénévole" />
                    </SelectTrigger>
                    <SelectContent>
                      {volunteers.map((volunteer) => (
                        <SelectItem key={volunteer.id} value={volunteer.id}>
                          {volunteer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {contacts.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucun contact</h3>
            <p className="text-slate-500">
              {user?.role === 'admin' 
                ? 'Commencez par ajouter des contacts à appeler' 
                : 'Aucun contact ne vous a été assigné pour le moment'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main App Layout
const AppLayout = ({ children }) => {
  // UTILISATEUR DÉMO POUR BYPASS AUTH
  const demoUser = {
    id: "demo-admin",
    name: "Démo Admin",
    email: "demo@brin.fr",
    role: "admin"
  };

  const user = demoUser; // Override user pour démo
  const logout = () => window.location.reload(); // Simple refresh pour démo
  
  const [activeTab, setActiveTab] = useState('dashboard');

  const navigation = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'calls', label: 'Appels', icon: Phone },
    { id: 'ai-chat', label: 'Chat IA', icon: Bot },
    { id: 'calendar', label: 'Planning', icon: Calendar },
  ];

  if (user?.role === 'admin') {
    navigation.push({ id: 'settings', label: 'Paramètres', icon: Settings });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white/80 backdrop-blur-sm border-r border-slate-200 min-h-screen">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">Brin de causette</h1>
                <p className="text-sm text-slate-500">Réseau Ezra</p>
              </div>
            </div>
          </div>
          
          <nav className="p-4">
            <div className="space-y-2">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
            </div>
          </nav>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-slate-50 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{user?.name}</span>
              </div>
              <p className="text-xs text-slate-500">{user?.email}</p>
              <Badge variant="secondary" className="mt-1 text-xs">
                {user?.role === 'admin' ? 'Administrateur' : 'Bénévole'}
              </Badge>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {activeTab === 'dashboard' && <Dashboard />}
          {activeTab === 'contacts' && <Contacts />}
          {activeTab === 'calls' && <div className="text-center py-12"><p className="text-slate-500">Section Appels - En développement</p></div>}
          {activeTab === 'ai-chat' && <AIChat />}
          {activeTab === 'calendar' && <div className="text-center py-12"><p className="text-slate-500">Section Planning - En développement</p></div>}
          {activeTab === 'settings' && <div className="text-center py-12"><p className="text-slate-500">Section Paramètres - En développement</p></div>}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const { token, loading } = useAuth();

  // AUTHENTIFICATION DÉSACTIVÉE TEMPORAIREMENT - ACCÈS DIRECT À L'APPLI
  const demoUser = {
    id: "demo-admin",
    name: "Démo Admin",
    email: "demo@brin.fr",
    role: "admin"
  };

  // Override auth context pour demo
  React.useEffect(() => {
    // Simuler une connexion admin pour la démo
    if (!token) {
      const { login } = useAuth();
      // login(demoUser, "demo-token");
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* ACCÈS DIRECT SANS AUTHENTIFICATION POUR DÉMO */}
          <Route path="/" element={<AppLayout />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

// App with Auth Provider - VERSION DÉMO SANS AUTH
export default function AppWithAuth() {
  // BYPASS AUTH PROVIDER POUR DÉMO
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isVolunteerView, setIsVolunteerView] = useState(false);
  
  const demoUser = {
    id: isVolunteerView ? "volunteer-demo" : "demo-admin",
    name: isVolunteerView ? "Sophie Dubois (Bénévole)" : "Démo Admin",
    email: isVolunteerView ? "sophie@brin.fr" : "demo@brin.fr", 
    role: isVolunteerView ? "volunteer" : "admin"
  };

  const navigation = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'contacts', label: 'Contacts', icon: Users },
    ...(demoUser.role === 'admin' ? [{ id: 'volunteers', label: 'Bénévoles', icon: UserCheck }] : []),
    { id: 'calls', label: 'Appels & Planning', icon: Phone },
    { id: 'ai-chat', label: 'Chat IA', icon: Bot },
    ...(demoUser.role === 'admin' ? [{ id: 'users', label: 'Utilisateurs', icon: Settings }] : [])
  ];

  return (
    <div className="App">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white/80 backdrop-blur-sm border-r border-slate-200 min-h-screen">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-800">Brin de causette</h1>
                  <p className="text-sm text-slate-500">Réseau Ezra</p>
                </div>
              </div>

              {/* Switch Admin/Bénévole */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">
                    Vue {isVolunteerView ? 'Bénévole' : 'Admin'}
                  </span>
                  <button
                    onClick={() => setIsVolunteerView(!isVolunteerView)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    {isVolunteerView ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  Cliquez pour basculer entre les vues
                </p>
              </div>
            </div>
            
            <nav className="p-4">
              <div className="space-y-2">
                {navigation.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>

            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">{demoUser.name}</span>
                </div>
                <p className="text-xs text-slate-500">{demoUser.email}</p>
                <Badge variant="secondary" className="mt-1 text-xs">
                  {demoUser.role === 'admin' ? 'Administrateur' : 'Bénévole'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-8">
            {activeTab === 'dashboard' && <Dashboard onNavigate={setActiveTab} />}
            {activeTab === 'contacts' && <Contacts />}
            {demoUser.role === 'admin' && activeTab === 'volunteers' && <Volunteers />}
            {activeTab === 'calls' && <CallsAndPlanning />}
            {activeTab === 'ai-chat' && <AIChat />}
            {demoUser.role === 'admin' && activeTab === 'users' && <UsersManagement />}
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}